<ng-container>
  <div class="tw-flex tw-w-full tw-justify-start">
    <form
      *ngIf="profile"
      #form
      [appApiAction]="formPromise"
      ngNativeValidate
      (ngSubmit)="submit()"
      class="tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-5"
    >
      <!--      photo-->
      <div
        class="tw-flex tw-w-full tw-items-center tw-gap-5 tw-rounded-xl tw-bg-color-surface-card tw-p-6 tw-shadow-elevation-card md:tw-flex-col md:tw-text-center"
      >
        <div class="tw-relative tw-inline-block tw-flex-shrink-0">
          <bit-avatar
            [text]="profile | userName"
            [id]="profile.id"
            [color]="avatarBackgroundColor"
            size="large"
            [imgSrc]="avatarSrc ?? undefined"
          ></bit-avatar>
          <!-- Decorative badge: aria-hidden + tabindex=-1 intentional — the "Change photo"
               text button below provides the keyboard/screen-reader affordance. -->
          <button
            type="button"
            aria-hidden="true"
            tabindex="-1"
            class="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-h-[30px] tw-w-[30px] tw-translate-x-1/4 tw-translate-y-1/4 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-color-surface-card tw-shadow-elevation-card"
            (click)="openAvatarFilePicker()"
          >
            <icon [name]="'camera'" [size]="4" [currentColor]="'color-icon-action'"></icon>
          </button>
          <input
            #avatarFileInput
            type="file"
            accept="image/*"
            class="tw-hidden"
            (change)="onAvatarFileSelected($event)"
          />
        </div>
        <div class="tw-min-w-0 tw-flex-1">
          <div class="tw-text-base tw-font-medium tw-text-color-text-default">
            {{ profile | userName }}
          </div>
          <div class="tw-truncate tw-text-[13px] tw-text-color-text-tertiary">
            {{ profile.email }}
          </div>
        </div>
        <button
          type="button"
          class="tw-h-9 tw-flex-shrink-0 tw-appearance-none tw-rounded-2xl tw-border-none tw-bg-color-surface-card tw-px-4 tw-text-[13px] tw-font-medium tw-text-color-text-action tw-shadow-elevation-card hover:tw-opacity-80"
          (click)="openAvatarFilePicker()"
        >
          {{ "changePhoto" | i18n }}
        </button>
      </div>

      <!--      personal information-->
      <div class="tw-w-full tw-rounded-xl tw-bg-color-surface-card tw-p-6 tw-shadow-elevation-card">
        <p class="tw-m-0 tw-mb-4 tw-text-base tw-font-semibold tw-text-color-text-default">
          {{ "personalInformation" | i18n }}
        </p>
        <div class="tw-flex tw-gap-5 md:tw-flex-col">
          <!--      name-->
          <div class="tw-w-1/2 md:tw-w-full">
            <app-input
              id="name"
              [label]="'name' | i18n"
              [type]="'text'"
              [isRequired]="true"
              [value]="profile.name"
              (outputValue)="onNameChange($event)"
            >
            </app-input>
          </div>
          <!--      email — intentionally not using app-input: the lock icon overlay
                   requires a custom layout that doesn't fit the standard app-input API
                   without significant component changes. Muted via tw-text-color-text-secondary
                   rather than opacity, so the address stays WCAG AA legible (readonly text,
                   not an inactive control, so contrast still applies). -->
          <div class="tw-flex tw-w-1/2 tw-flex-col tw-gap-1.5 md:tw-w-full">
            <label
              for="email"
              class="tw-pointer-events-none tw-font-bold tw-text-[12px] tw-text-color-text-tertiary"
              >{{ "email" | i18n }}</label
            >
            <div class="tw-relative">
              <input
                id="email"
                type="text"
                name="Email"
                class="tw-h-12 tw-w-full tw-rounded-xl tw-border-none tw-bg-color-surface-input tw-pl-6 tw-pr-10 tw-text-sm tw-text-color-text-secondary tw-cursor-default"
                [ngModel]="profile.email"
                readonly
              />
              <icon
                [name]="'lock'"
                [size]="4"
                [currentColor]="'color-text-tertiary'"
                class="tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2"
              ></icon>
            </div>
          </div>
        </div>
      </div>

      <!--      fingerprint phrase-->
      <div
        *ngIf="fingerprint"
        class="tw-w-full tw-rounded-xl tw-bg-color-surface-card tw-p-6 tw-shadow-elevation-card"
      >
        <p class="tw-m-0 tw-mb-4 tw-text-base tw-font-semibold tw-text-color-text-default">
          {{ "accountSecurity" | i18n }}
        </p>
        <div class="tw-flex tw-flex-col tw-gap-1.5">
          <label class="tw-pointer-events-none tw-text-[13px] tw-text-color-text-tertiary">{{
            "yourAccountsFingerprint" | i18n
          }}</label>
          <div class="tw-relative">
            <div
              class="tw-flex tw-min-h-[48px] tw-items-center tw-cursor-default tw-select-all tw-break-all tw-rounded-xl tw-border tw-border-color-border-subtle tw-bg-color-surface-input tw-py-3 tw-pl-6 tw-pr-14 tw-font-mono tw-text-sm tw-leading-relaxed tw-text-color-text-default"
            >
              {{ fingerprint }}
            </div>
            <button
              type="button"
              class="tw-absolute tw-right-1.5 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-bg-color-surface-card tw-shadow-elevation-card"
              [attr.aria-label]="'copyValue' | i18n"
              (click)="copyFingerprint()"
            >
              <icon [name]="'copy-06'" [size]="4" [currentColor]="'color-icon-action'"></icon>
            </button>
          </div>
          <p class="tw-m-0 tw-text-xs tw-text-color-text-disabled">
            {{ "fingerprintEnsureIntegrityVerify" | i18n }}
          </p>
        </div>
      </div>

      <!-- DS 2.1 migration: using a native button here instead of app-gloss-button to match
           the neumorphic card layout. Other settings pages still use app-gloss-button and
           will be migrated separately. -->
      <button
        type="submit"
        class="tw-h-12 tw-w-full tw-rounded-2xl tw-border-none tw-bg-color-icon-action tw-px-8 tw-text-sm tw-font-bold tw-text-color-text-on-action tw-shadow-elevation-card tw-cursor-pointer disabled:tw-cursor-default disabled:tw-opacity-60"
        [attr.disabled]="form.loading || null"
      >
        {{ (form.loading ? "savingChanges" : "saveChanges") | i18n }}
      </button>
    </form>
  </div>
</ng-container>
